import { z } from "zod";

export const UserProfileSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  name: z.string().min(1, { message: "Name cannot be empty" }).nullable(),
  username: z.string().optional().nullable(),
  displayUsername: z.string().optional().nullable(),
  emailVerified: z.boolean().optional(),
  avatarUrl: z.string().url().optional().nullable(),
});

export type UserProfile = z.infer<typeof UserProfileSchema>;

export const UpdateUserProfileInputSchema = z.object({
  name: z.string().min(1, { message: "Name cannot be empty" }),
  avatarUrl: z.string().url().optional().nullable(),
});
export type UpdateUserProfileInput = z.infer<
  typeof UpdateUserProfileInputSchema
>;

export const ChangePasswordInputSchema = z
  .object({
    currentPassword: z
      .string()
      .min(1, { message: "Current password is required." }),
    newPassword: z
      .string()
      .min(8, { message: "New password must be at least 8 characters long" }),
  })
  .refine((data) => data.currentPassword !== data.newPassword, {
    message: "New password must be different from the current password.",
    path: ["newPassword"],
  });
export type ChangePasswordInput = z.infer<typeof ChangePasswordInputSchema>;

export const PasskeyCredentialIDSchema = z.object({
  credentialID: z.string().min(1, { message: "Credential ID is required." }),
});
export type PasskeyCredentialIDInput = z.infer<
  typeof PasskeyCredentialIDSchema
>;

export const SessionTokenSchema = z.object({
  sessionToken: z.string().min(1, { message: "Session token is required." }),
});
export type SessionTokenInput = z.infer<typeof SessionTokenSchema>;

export const UserSessionSchema = z.object({
  id: z.string(),
  userId: z.string(),
  token: z.string(),
  expiresAt: z.date(),
  ipAddress: z.string().optional().nullable(),
  userAgent: z.string().optional().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  isCurrent: z.boolean().optional(),
});
export type UserSession = z.infer<typeof UserSessionSchema>;

export const ListSessionsOutputSchema = z.array(UserSessionSchema);
export type ListSessionsOutput = z.infer<typeof ListSessionsOutputSchema>;

export const ApiKeyIdInputSchema = z.object({
  id: z.string().min(1, { message: "API Key ID is required." }),
});
export type ApiKeyIdInput = z.infer<typeof ApiKeyIdInputSchema>;

export const ApiKeyDetailsSchema = z.object({
  id: z.string(),
  name: z.string(),
  keyPrefix: z.string().optional().nullable(),
  expiresAt: z.date().nullable(),
  createdAt: z.date(),
  lastUsedAt: z.date().optional().nullable(),
  userId: z.string(),
});
export type ApiKeyDetails = z.infer<typeof ApiKeyDetailsSchema>;

export const UpdateApiKeyInputSchema = z.object({
  id: z.string().min(1, { message: "API Key ID is required." }),
  name: z.string().min(1, { message: "Name cannot be empty" }).optional(),
});
export type UpdateApiKeyInput = z.infer<typeof UpdateApiKeyInputSchema>;

export const ListApiKeysOutputSchema = z.array(
  ApiKeyDetailsSchema.omit({ userId: true }),
);
export type ListApiKeysOutput = z.infer<typeof ListApiKeysOutputSchema>;

export const AdminSendNotificationInputSchema = z.object({
  userId: z.string().min(1, { message: "User ID is required." }),
  message: z.string().min(1, { message: "Message cannot be empty." }).max(1000, { message: "Message cannot exceed 1000 characters." }),
});
export type AdminSendNotificationInput = z.infer<typeof AdminSendNotificationInputSchema>;
